import { useCallback } from 'react';
import { ApolloCache, ApolloError, useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  GroupTeacherTermsQuery,
  TeacherBillingEntitiesListQuery,
  TeacherBillingEntityInput,
  useAddTeacherBillingEntityMutation,
  useEditTeacherBillingEntityMutation,
  useGroupTeacherTermsQuery,
  useMakeDefaultTeacherBillingEntityMutation,
  useStoreApplicationFinanceTermsMutation,
  useTeacherBillingEntitiesListQuery,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { isPermissionError } from 'core/utils/isPermissionError';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import {
  applicationTermsVariables,
  billingErrorKey,
  canManageTeacherTerms,
  IApplicationTermsDraft,
} from './teacherBillingModel';

/**
 * ТЗ 0114 (#158) — реквизиты преподавателя для фактур и финансовые условия
 * заявок. Скрытие кнопок — UX: каждую запись сервер закрывает
 * `TEACHER_TERMS_ROLES`.
 */

export type ITeacherBillingEntity = TeacherBillingEntitiesListQuery['teacherBillingEntities'][number];
export type IGroupTeacherTerms = GroupTeacherTermsQuery['groupTeacherApplications'][number];

const EMPTY_ENTITIES: ITeacherBillingEntity[] = [];
const EMPTY_TERMS: IGroupTeacherTerms[] = [];

export const useTeacherTermsPermission = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  return canManageTeacherTerms((userState.me?.roles ?? []) as string[]);
};

/** Реквизиты преподавателя (по умолчанию — только действующие). */
export const useTeacherBillingEntities = (
  teacherId: string | null | undefined,
  { includeInactive = false, skip = false } = {},
) => {
  const { data, loading, error, refetch } = useTeacherBillingEntitiesListQuery({
    variables: { teacherId: teacherId || '', includeInactive },
    skip: skip || !teacherId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    entities: data?.teacherBillingEntities ?? EMPTY_ENTITIES,
    loading,
    error,
    forbidden: isPermissionError(error),
    refetch,
  };
};

/** Заявки преподавателей группы с условиями. */
export const useGroupTeacherTerms = (groupId: string, skip = false) => {
  const { data, loading, error, refetch } = useGroupTeacherTermsQuery({
    variables: { groupId },
    skip: skip || !groupId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    applications: data?.groupTeacherApplications ?? EMPTY_TERMS,
    loading,
    error,
    forbidden: isPermissionError(error),
    refetch,
  };
};

export interface IBillingFieldError {
  field: string;
  code: string;
  message: string;
}

export interface IBillingResult {
  ok: boolean;
  errors: IBillingFieldError[];
}

const toErrors = (
  raw:
    | Array<{
        field?: string | null;
        code?: string | null;
        message?: string | null;
      } | null>
    | null
    | undefined,
): IBillingFieldError[] =>
  (raw ?? [])
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => ({
      field: item.field ?? '',
      code: item.code ?? '',
      message: item.message ?? '',
    }));

/**
 * После удачной записи — списки реквизитов и условия групп перечитываются
 * (поля с аргументами на ROOT_QUERY выбрасываются целиком).
 */
const evictBilling = (
  cache: Pick<ApolloCache<unknown>, 'evict' | 'gc'>,
  { data }: { data?: unknown },
): void => {
  const payload = data
    ? (Object.values(data as Record<string, unknown>)[0] as
        | { success?: boolean | null }
        | null
        | undefined)
    : null;
  if (!payload?.success) return;
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'teacherBillingEntities' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'groupTeacherApplications' });
  cache.gc();
};

/** Текст отказа сервера на языке интерфейса. */
export const useBillingErrorText = () => {
  const { t } = useTranslation();
  return useCallback(
    (error: {
      field?: string | null;
      code?: string | null;
      message?: string;
    }) => {
      const key = billingErrorKey(error);
      return key ? t(key) : error.message || t('teacherBilling.errors.generic');
    },
    [t],
  );
};

const useBillingToast = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) =>
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.TeacherEdit,
      }),
    [addToast],
  );
  const crash = useCallback(
    (caught: unknown): IBillingResult => {
      notify(
        ToastTypes.error,
        t('teacherBilling.toast.failed'),
        caught instanceof ApolloError && isPermissionError(caught)
          ? t('teacherBilling.errors.forbidden')
          : t('teacherBilling.toast.network'),
      );
      return { ok: false, errors: [] };
    },
    [notify, t],
  );
  return { notify, crash };
};

/** Записи реквизитов: добавить, править (вкл. выключить), сделать основным. */
export const useTeacherBillingActions = () => {
  const { t } = useTranslation();
  const { notify, crash } = useBillingToast();
  const errorText = useBillingErrorText();
  const [
    createMutation,
    { loading: creating },
  ] = useAddTeacherBillingEntityMutation({ update: evictBilling });
  const [
    updateMutation,
    { loading: updating },
  ] = useEditTeacherBillingEntityMutation({ update: evictBilling });
  const [
    defaultMutation,
    { loading: settingDefault },
  ] = useMakeDefaultTeacherBillingEntityMutation({ update: evictBilling });

  const create = useCallback(
    async (
      teacherId: string,
      input: TeacherBillingEntityInput,
    ): Promise<IBillingResult> => {
      try {
        const { data } = await createMutation({
          variables: { teacherId, input },
        });
        const payload = data?.createTeacherBillingEntity;
        if (!payload?.success) {
          return { ok: false, errors: toErrors(payload?.customErrors) };
        }
        notify(ToastTypes.success, t('teacherBilling.toast.created'));
        return { ok: true, errors: [] };
      } catch (caught) {
        return crash(caught);
      }
    },
    [createMutation, crash, notify, t],
  );

  const update = useCallback(
    async (
      id: string,
      input: TeacherBillingEntityInput,
    ): Promise<IBillingResult> => {
      try {
        const { data } = await updateMutation({ variables: { id, input } });
        const payload = data?.updateTeacherBillingEntity;
        if (!payload?.success) {
          return { ok: false, errors: toErrors(payload?.customErrors) };
        }
        notify(ToastTypes.success, t('teacherBilling.toast.saved'));
        return { ok: true, errors: [] };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, notify, t, updateMutation],
  );

  /** Отказ без окна (кнопка строки) — тостом. */
  const reportRow = useCallback(
    (errors: IBillingFieldError[]) =>
      notify(
        ToastTypes.error,
        t('teacherBilling.toast.failed'),
        errors.map(errorText).filter(Boolean).join('\n') || undefined,
      ),
    [errorText, notify, t],
  );

  const setDefault = useCallback(
    async (id: string): Promise<IBillingResult> => {
      try {
        const { data } = await defaultMutation({ variables: { id } });
        const payload = data?.setDefaultTeacherBillingEntity;
        if (!payload?.success) {
          const errors = toErrors(payload?.customErrors);
          reportRow(errors);
          return { ok: false, errors };
        }
        notify(ToastTypes.success, t('teacherBilling.toast.defaultSet'));
        return { ok: true, errors: [] };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, defaultMutation, notify, reportRow, t],
  );

  const setActive = useCallback(
    async (id: string, isActive: boolean): Promise<IBillingResult> => {
      try {
        const { data } = await updateMutation({
          variables: { id, input: { isActive } },
        });
        const payload = data?.updateTeacherBillingEntity;
        if (!payload?.success) {
          const errors = toErrors(payload?.customErrors);
          reportRow(errors);
          return { ok: false, errors };
        }
        notify(
          ToastTypes.success,
          isActive
            ? t('teacherBilling.toast.activated')
            : t('teacherBilling.toast.deactivated'),
        );
        return { ok: true, errors: [] };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, notify, reportRow, t, updateMutation],
  );

  return {
    create,
    update,
    setDefault,
    setActive,
    busy: { creating, updating, settingDefault },
  };
};

/** Финансовые условия заявки (узкая мутация, не вся заявка). */
export const useApplicationFinanceTerms = () => {
  const { t } = useTranslation();
  const { notify, crash } = useBillingToast();
  const [
    mutation,
    { loading: saving },
  ] = useStoreApplicationFinanceTermsMutation({ update: evictBilling });
  const save = useCallback(
    async (
      applicationId: string,
      draft: IApplicationTermsDraft,
      extra: { servicePrice?: number | null; currency?: string | null } = {},
      { quiet = false }: { quiet?: boolean } = {},
    ): Promise<IBillingResult> => {
      try {
        const { data } = await mutation({
          variables: applicationTermsVariables(applicationId, draft, extra) as {
            applicationId: string;
          },
        });
        const payload = data?.setApplicationFinanceTerms;
        if (!payload?.success) {
          return { ok: false, errors: toErrors(payload?.customErrors) };
        }
        if (!quiet)
          notify(ToastTypes.success, t('teacherBilling.toast.termsSaved'));
        return { ok: true, errors: [] };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, mutation, notify, t],
  );
  return { save, saving };
};
